"use client";

// Liste d'attente d'une formation à venir.
//
// Deux publics, un seul bouton visible à la fois :
// - membre connecté : un clic suffit, son adresse est celle de son compte ;
// - visiteur : un champ e-mail, sans création de compte imposée. Réclamer une
//   inscription à quelqu'un qui veut juste être prévenu ferait perdre la
//   moitié des demandes, et c'est précisément ce public qu'on cherche à
//   mesurer.
//
// Deux habillages, choisis par `variant` :
// - "card" (colonne de droite) : le bloc titre + explication + bouton plein,
//   seul contenu de sa colonne, donc légitime à l'occuper entièrement ;
// - "inline" (bandeau de la fiche) : un bouton au trait à la place du bouton
//   d'achat. Le même bloc y écrasait la hiérarchie de la page et répétait une
//   information déjà donnée. Le visiteur y saisit son adresse dans une modale,
//   pour ne pas réintroduire un formulaire en haut de page.
//
// L'inscription est relue au chargement : sans ça, un rafraîchissement redonne
// le bouton intact et personne ne sait s'il a déjà fait sa demande. La base
// fait foi pour un membre ; un visiteur n'étant identifiable par aucun serveur,
// son navigateur garde le souvenir de sa demande.
//
// L'état « déjà inscrit » n'est pas une erreur : le back répond 200 et on
// affiche le même remerciement, sans jamais dire à un visiteur si telle adresse
// est connue de la plateforme.

import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { motion } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";

const storageKey = (courseSlug) => `believemy_waitlist_${courseSlug}`;

// Le navigateur est la seule mémoire disponible pour un visiteur : aucune
// session ne permet de le reconnaître au retour sur la page.
function readRemembered(courseSlug) {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(storageKey(courseSlug));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.at ? parsed : { at: null };
    } catch (error) {
        return null;
    }
}

function remember(courseSlug, at) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(
            storageKey(courseSlug),
            JSON.stringify({ at: at || new Date().toISOString() }),
        );
    } catch (error) {
        // Navigation privée ou stockage plein : l'inscription est en base, seul
        // le rappel visuel est perdu.
    }
}

export default function CourseWaitlist({
    courseSlug,
    courseName,
    variant = "card",
}) {
    const t = useTranslations();
    const locale = useLocale();
    const { data: session, status } = useSession();

    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | error
    const [errorMessage, setErrorMessage] = useState(null);
    const [joined, setJoined] = useState(false);
    const [joinedAt, setJoinedAt] = useState(null);
    const [checked, setChecked] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const isMember = Boolean(session?.user);
    const isInline = variant === "inline";
    const userId = session?.user?.userId;

    // Lecture de l'inscription existante. Pour un membre, la base tranche : un
    // souvenir de navigateur laissé sous une autre adresse ne doit pas lui
    // faire croire qu'il est sur la liste.
    useEffect(() => {
        if (status === "loading") return;

        let cancelled = false;

        if (!userId) {
            const remembered = readRemembered(courseSlug);
            if (remembered) {
                setJoined(true);
                setJoinedAt(remembered.at);
            }
            setChecked(true);
            return undefined;
        }

        (async () => {
            try {
                const response = await fetch("/api/course-waitlist/status", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ courseSlug }),
                });
                const data = await response.json();
                if (cancelled) return;
                if (data?.joined) {
                    setJoined(true);
                    setJoinedAt(data.createdAt || null);
                }
            } catch (error) {
                // On laisse le bouton proposé : une demande en double est sans
                // effet en base, alors que masquer le bouton à tort supprimerait
                // le seul moyen de s'inscrire.
            } finally {
                if (!cancelled) setChecked(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [courseSlug, userId, status]);

    const submit = useCallback(
        async (event) => {
            event?.preventDefault();
            if (state === "loading") return;

            setState("loading");
            setErrorMessage(null);

            try {
                const response = await fetch("/api/course-waitlist/join", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        courseSlug,
                        courseName,
                        email: isMember ? undefined : email.trim(),
                    }),
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    setErrorMessage(data?.message || t("courseWaitlist_error"));
                    setState("error");
                    return;
                }

                const data = await response.json().catch(() => ({}));
                const at = data?.createdAt || new Date().toISOString();

                remember(courseSlug, at);
                setJoinedAt(at);
                setJoined(true);
                setState("idle");
                setModalOpen(false);
            } catch (error) {
                setErrorMessage(t("courseWaitlist_error"));
                setState("error");
            }
        },
        [courseName, courseSlug, email, isMember, state, t],
    );

    // La date n'a sa place que dans la carte : le bandeau reste sur une ligne.
    const joinedCardLabel = joinedAt
        ? t("courseWaitlist_joined_since", {
              date: new Intl.DateTimeFormat(locale, {
                  day: "numeric",
                  month: "long",
              }).format(new Date(joinedAt)),
          })
        : t("courseWaitlist_joined_hint");

    // Formulaire du visiteur, partagé par la carte et par la modale du bandeau.
    const guestForm = (
        <form onSubmit={submit}>
            <label
                htmlFor={`waitlist-email-${courseSlug}`}
                className="mb-1 block text-xs font-medium text-gray-500 dark:text-grayDark-400"
            >
                {t("courseWaitlist_email_label")}
            </label>
            <div className={isInline ? "" : "flex flex-col gap-2 sm:flex-row"}>
                <input
                    id={`waitlist-email-${courseSlug}`}
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("courseWaitlist_email_placeholder")}
                    className={`min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-hidden transition-colors focus:border-primary dark:border-white/10 dark:bg-white/[0.04] dark:text-white ${
                        isInline ? "w-full" : ""
                    }`}
                />
                <button
                    type="submit"
                    disabled={state === "loading"}
                    className={`press-safe shrink-0 rounded-2xl border-b-4 border-b-orange-700 bg-orange-500 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-orange-600 active:translate-y-1 active:border-b-transparent disabled:cursor-not-allowed disabled:opacity-60 ${
                        isInline ? "mt-3 w-full" : ""
                    }`}
                >
                    {state === "loading"
                        ? t("courseWaitlist_loading")
                        : t("courseWaitlist_button_guest")}
                </button>
            </div>
        </form>
    );

    const errorLine =
        state === "error" && errorMessage ? (
            <p
                role="alert"
                className="mb-0 mt-3 text-sm text-red-600 dark:text-red-400"
            >
                {errorMessage}
            </p>
        ) : null;

    if (isInline) {
        // Le temps de la lecture, une empreinte de la taille du bouton : sans
        // elle, le bouton s'affiche puis se transforme en confirmation sous les
        // yeux de quelqu'un déjà inscrit.
        if (!checked) {
            return (
                <div className="h-[46px] w-[230px] max-w-full animate-pulse rounded-xl bg-gray-100 dark:bg-white/[0.06]" />
            );
        }

        if (joined) {
            return (
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex flex-wrap items-center gap-x-4 gap-y-2"
                >
                    <span className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400">
                        <SolarIcon
                            name="check-circle-bold"
                            className="text-lg"
                        />
                        {t("courseWaitlist_joined")}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-grayDark-400">
                        {t("courseWaitlist_joined_hint")}
                    </span>
                </motion.div>
            );
        }

        return (
            <>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button
                        type="button"
                        onClick={() =>
                            isMember ? submit() : setModalOpen(true)
                        }
                        disabled={state === "loading"}
                        className="inline-flex items-center gap-2 rounded-xl border border-primary px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <SolarIcon name="bell-linear" className="text-lg" />
                        {state === "loading"
                            ? t("courseWaitlist_loading")
                            : t("courseWaitlist_button_notify")}
                    </button>
                    <span className="text-sm text-gray-500 dark:text-grayDark-400">
                        {t("courseWaitlist_coming_soon")}
                    </span>
                </div>

                {/* L'échec d'un visiteur se dit dans la modale, là où se trouve
                    son formulaire, pas une deuxième fois derrière elle. */}
                {isMember ? errorLine : null}

                <BottomSheetModal
                    isOpen={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setState("idle");
                        setErrorMessage(null);
                    }}
                    labelledBy={`waitlist-modal-title-${courseSlug}`}
                >
                    <div className="p-8">
                        <p
                            id={`waitlist-modal-title-${courseSlug}`}
                            className="mb-0 text-lg font-bold text-gray-900 dark:text-white"
                        >
                            {t("courseWaitlist_modal_title")}
                        </p>
                        <p className="mb-0 mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                            {t("courseWaitlist_modal_subtitle")}
                        </p>

                        <div className="mt-5">{guestForm}</div>

                        {errorLine}

                        <p className="mb-0 mt-3 text-xs text-gray-400 dark:text-grayDark-500">
                            {t("courseWaitlist_privacy")}
                        </p>
                    </div>
                </BottomSheetModal>
            </>
        );
    }

    if (!checked) {
        return (
            <div className="h-[188px] w-full animate-pulse rounded-2xl bg-gray-100 dark:bg-white/[0.06]" />
        );
    }

    if (joined) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-500/20 dark:bg-emerald-500/10"
            >
                <p className="mb-0 flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-400">
                    <SolarIcon name="check-circle-bold" className="text-xl" />
                    {t("courseWaitlist_joined")}
                </p>
                <p className="mb-0 mt-1 text-sm text-emerald-700/80 dark:text-emerald-400/80">
                    {joinedCardLabel}
                </p>
            </motion.div>
        );
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]">
            <p className="mb-0 font-semibold text-gray-900 dark:text-white">
                {t("courseWaitlist_title")}
            </p>
            <p className="mb-0 mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                {t("courseWaitlist_subtitle")}
            </p>

            {isMember ? (
                <button
                    type="button"
                    onClick={submit}
                    disabled={state === "loading" || status === "loading"}
                    className="press-safe mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border-b-4 border-b-orange-700 bg-orange-500 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-orange-600 active:translate-y-1 active:border-b-transparent disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {state === "loading"
                        ? t("courseWaitlist_loading")
                        : t("courseWaitlist_button_member")}
                </button>
            ) : (
                <div className="mt-4">{guestForm}</div>
            )}

            {errorLine}

            <p className="mb-0 mt-3 text-xs text-gray-400 dark:text-grayDark-500">
                {t("courseWaitlist_privacy")}
            </p>
        </div>
    );
}
